import fetchConfig from './fetch-config';

class B2bLinks extends HTMLElement {
  private links: HTMLAnchorElement[] = [];
  private toggleBtn: HTMLButtonElement | null = null;
  private mobileContent: HTMLElement | null = null;
  private isMobile = false;
  private boundKeydown: (e: KeyboardEvent) => void;
  private boundOutsideClick: (e: MouseEvent) => void;

  constructor() {
    super();
    this.boundKeydown = this.handleKeydown.bind(this);
    this.boundOutsideClick = this.handleOutsideClick.bind(this);
  }

  connectedCallback(): void {
    this.links = Array.from(this.querySelectorAll<HTMLAnchorElement>('[data-b2b-link]'));
    this.syncActiveFromDocument();
    this.links.forEach((link) => {
      link.addEventListener('click', this.handleClick);
      link.addEventListener('keydown', this.handleLinkKeydown);
    });

    this.isMobile = this.classList.contains('b2b-links-mobile');
    if (this.isMobile) {
      this.toggleBtn = this.querySelector('button');
      this.mobileContent = this.querySelector('.b2b-links-mobile-content');
      this.setMobileContentInert(true);
      this.toggleBtn?.addEventListener('click', this.handleToggleClick);
      this.addEventListener('focusout', this.handleFocusOut);
    }
  }

  disconnectedCallback(): void {
    this.links.forEach((link) => {
      link.removeEventListener('click', this.handleClick);
      link.removeEventListener('keydown', this.handleLinkKeydown);
    });
    this.links = [];

    if (this.isMobile) {
      this.toggleBtn?.removeEventListener('click', this.handleToggleClick);
      this.removeEventListener('focusout', this.handleFocusOut);
      document.removeEventListener('keydown', this.boundKeydown);
      document.removeEventListener('click', this.boundOutsideClick);
    }
  }

  private syncActiveFromDocument(): void {
    const isB2b = document.documentElement.dataset.isB2b === 'true';

    this.links.forEach((link) => {
      const linkIsB2b = link.getAttribute('data-b2b-value') === 'true';
      link.classList.toggle('active', linkIsB2b === isB2b);
    });
  }

  private setActiveLink(activeLink: HTMLAnchorElement): void {
    this.links.forEach((link) => {
      link.classList.toggle('active', link === activeLink);
    });
  }

  private setMobileContentInert(inert: boolean): void {
    if (!this.mobileContent) return;

    if (inert) {
      this.mobileContent.setAttribute('inert', '');
      this.mobileContent.setAttribute('aria-hidden', 'true');
    } else {
      this.mobileContent.removeAttribute('inert');
      this.mobileContent.setAttribute('aria-hidden', 'false');
    }
  }

  private handleToggleClick = (e: MouseEvent): void => {
    e.stopPropagation();

    if (this.hasAttribute('data-open')) {
      this.close(true);
    } else {
      this.open();
    }
  };

  private open(): void {
    this.setAttribute('data-open', '');
    this.toggleBtn?.setAttribute('aria-expanded', 'true');
    this.setMobileContentInert(false);
    document.addEventListener('keydown', this.boundKeydown);
    document.addEventListener('click', this.boundOutsideClick);
  }

  private close(returnFocus = false): void {
    if (!this.hasAttribute('data-open')) return;

    this.removeAttribute('data-open');
    this.toggleBtn?.setAttribute('aria-expanded', 'false');
    this.setMobileContentInert(true);
    document.removeEventListener('keydown', this.boundKeydown);
    document.removeEventListener('click', this.boundOutsideClick);

    if (returnFocus) {
      this.toggleBtn?.focus();
    }
  }

  private getMobileContentFocusables(): HTMLElement[] {
    if (!this.mobileContent) return [];

    return Array.from(
      this.mobileContent.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    );
  }

  private handleTabKey(e: KeyboardEvent): void {
    if (!this.mobileContent?.contains(document.activeElement)) return;

    const focusable = this.getMobileContentFocusables();
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  private handleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this.close(true);
      return;
    }

    if (e.key === 'Tab') {
      this.handleTabKey(e);
    }
  }

  private handleOutsideClick(e: MouseEvent): void {
    if (!this.contains(e.target as Node)) {
      this.close(true);
    }
  }

  private handleFocusOut(): void {
    requestAnimationFrame(() => {
      if (!this.hasAttribute('data-open')) return;

      if (!this.contains(document.activeElement)) {
        this.close(false);
      }
    });
  }

  private handleLinkKeydown = (event: KeyboardEvent): void => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      (event.currentTarget as HTMLAnchorElement).click();
    }
  };

  private handleClick = async (event: MouseEvent): Promise<void> => {
    const link = event.currentTarget as HTMLAnchorElement;
    event.preventDefault();

    this.setActiveLink(link);

    if (this.isMobile) {
      this.close(false);
    }

    const b2bValue = link.getAttribute('data-b2b-value') ?? '';
    const isLoggedIn = document.documentElement.dataset.customerLoggedIn === 'true';

    try {
      const response = await fetch(window.routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          attributes: {
            b2b: b2bValue,
            b2b_manual: isLoggedIn ? 'true' : '',
          },
        }),
      });

      if (response.ok) {
        if (link.getAttribute('href') === '#' || link.getAttribute('href') === '') {
          window.location.reload();
        } else {
          window.location.href = link.href;
        }
      }
    } catch {
      // no redirect on failure
    }
  };
}

if (!customElements.get('b2b-links')) {
  customElements.define('b2b-links', B2bLinks);
}

function forceB2bOffWhenDisabled(): void {
  if (!document.querySelector('[data-b2b-disabled]')) return;
  if (document.documentElement.dataset.isB2b !== 'true') return;

  const isLoggedIn = document.documentElement.dataset.customerLoggedIn === 'true';
  const cartUpdateUrl = window.routes?.cart_update_url;
  if (!cartUpdateUrl) return;

  fetch(cartUpdateUrl, {
    ...fetchConfig(),
    body: JSON.stringify({
      attributes: {
        b2b: '',
        b2b_manual: isLoggedIn ? 'true' : '',
      },
    }),
  })
    .then((response) => {
      if (response.ok) window.location.reload();
    })
    .catch(() => {
      // leave the page as-is if the cart update fails
    });
}

forceB2bOffWhenDisabled();
